import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useWebSocketContext } from '@shared/context/WebSocketProvider';
import { useProfile } from '@shared/api/useProfile';
import { useChatStore, wsBuilder, PENDING_MESSAGE_TYPE } from '@entities/messanger';

// Общая отправка интерактивного предложения с оптимистичным сообщением
export const useSendOffer = (chatId) => {
  const { sendJsonMessage } = useWebSocketContext();
  const { data: profile } = useProfile();

  const send = useCallback(
    (type, buildCommand, payload) => {
      if (!chatId) return;

      const tempId = `temp-${type}-${Date.now()}`;

      useChatStore.getState().addOptimisticMessage({
        id: tempId,
        chat_id: chatId,
        sender_id: profile?.id,
        data: '',
        attachments: null,
        type,
        payload,
        timestamp: Math.floor(Date.now() / 1000),
        is_read: false,
        pending: true,
        failed: false,
      });

      try {
        const command = buildCommand();
        // Связываем id команды с tempId — если сервер ответит
        // warnings/success:false, пометим это сообщение failed
        useChatStore.getState().registerCommandTempId(command.id, tempId);
        sendJsonMessage(command);
      } catch (e) {
        useChatStore.getState().failOptimisticMessage(tempId);
        toast.error('Не удалось отправить предложение');
      }
    },
    [chatId, sendJsonMessage, profile?.id]
  );

  return send;
};

// Предложение слота (дата/время/инструмент)
export const useSendSlotOffer = (chatId) => {
  const send = useSendOffer(chatId);

  return useCallback(
    ({ date, time, instrument_id, instrument_name }) => {
      const payload = { date, time, instrument_id };
      if (instrument_name) payload.instrument_name = instrument_name;

      send(
        PENDING_MESSAGE_TYPE.SLOT_OFFER,
        () => wsBuilder.slotOffer(chatId, { date, time, instrument_id }),
        payload
      );
    },
    [send, chatId]
  );
};

// Предложение пакета занятий
export const useSendPackageOffer = (chatId) => {
  const send = useSendOffer(chatId);

  return useCallback(
    ({ offer_id, title, lessons_count, price }) => {
      const payload = { offer_id };
      if (title) payload.title = title;
      if (lessons_count) payload.lessons_count = lessons_count;
      if (price) payload.price = price;

      send(
        PENDING_MESSAGE_TYPE.PACKAGE_OFFER,
        () => wsBuilder.packageOffer(chatId, offer_id),
        payload
      );
    },
    [send, chatId]
  );
};
